import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserProvider';
import LoadingScreen from '../components/LoadingScreen';
import {
  Button,
  Link,
  Typography,
  TextField,
  Box,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import { loginWithGoogle, loginWithApple } from '../firebase';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import ApiClient from '../ApiClient';

const SocialButton = ({ icon: Icon, text, onClick, color, ...props }) => (
  <Button
    variant="outlined"
    fullWidth
    startIcon={<Icon />}
    onClick={onClick}
    sx={{
      height: 48,
      borderColor: 'rgba(255, 255, 255, 0.23)',
      color: 'white',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      ...props.sx,
    }}
    {...props}
  >
    {text}
  </Button>
);

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const apiClient = new ApiClient();
  const { setUser } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSocialLogin = async (loginFunction) => {
    setLoading(true);
    setErrors('');

    try {
      const user = await loginFunction();
      const loginResult = await login(user.email, user.uid);

      if (loginResult) {
        let next = '/';
        if (location.state && location.state.next) {
          next = location.state.next;
        }
        navigate(next === '/' ? '/home' : next);
      }
    } catch (error) {
      console.error('Social login error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setErrors('Login cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setErrors(
          'Popup was blocked by your browser. Please allow popups for this site.'
        );
      } else if (error.code === 'auth/cancelled-popup-request') {
        setErrors('Login cancelled. Please try again.');
      } else {
        setErrors('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInternalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors('');

    try {
      const response = await apiClient.requestUnauthenticated({
        method: 'POST',
        url: '/tokens',
        headers: {
          Authorization: 'Basic ' + btoa(`${username}:${password}`),
        },
      });
      if (response.ok) {
        // Store the tokens
        localStorage.setItem('accessToken', response.body.access_token);
        if (response.body.refresh_token) {
          localStorage.setItem('refreshToken', response.body.refresh_token);
        }

        // Use the regular request method which includes the token
        const userResponse = await apiClient.request({
          method: 'GET',
          url: '/me',
        });

        if (userResponse.ok) {
          console.log('Setting user state...');
          // Add auth provider to user object
          const userWithAuth = {
            ...userResponse.body,
            auth_provider: 'internal', // Mark this as internal auth
          };
          // Wait for the user state to be updated
          await setUser(userWithAuth);

          // Add a small delay to ensure state updates are complete
          await new Promise((resolve) => setTimeout(resolve, 100));

          navigate('/home', { replace: true });
        } else {
          console.error('Failed to get user info:', userResponse);
        }
      } else {
        if (response.status === 401) {
          setErrors('Invalid username or password. Please try again.');
        } else if (response.status === 429) {
          setErrors('Too many failed login attempts. Please try again later.');
        } else {
          setErrors('Something went wrong. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingScreen />}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, md: 5 },
        }}
        color={'white'}
      >
        <Typography variant="h5" mb={3}>
          Glad to have you back,
          <br />
          let&apos;s dive into some new music!
        </Typography>

        <Stack spacing={2} mb={3}>
          <SocialButton
            icon={GoogleIcon}
            text="Continue with Google"
            onClick={() => handleSocialLogin(loginWithGoogle)}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              borderColor: '#fff',
              '&:hover': {
                backgroundColor: '#1a1a1a',
                borderColor: '#fff',
              },
            }}
          />
          <SocialButton
            icon={AppleIcon}
            text="Continue with Apple"
            onClick={() => handleSocialLogin(loginWithApple)}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              borderColor: '#fff',
              '&:hover': {
                backgroundColor: '#1a1a1a',
                borderColor: '#fff',
              },
            }}
          />
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
            or
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <form onSubmit={handleInternalLogin} autoComplete="off">
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            margin="normal"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{
              autoComplete: 'off',
              autoCapitalize: 'off',
            }}
            required
          />
          <TextField
            label="Password"
            variant="outlined"
            fullWidth
            type="password"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{
              autoComplete: 'off',
              autoCapitalize: 'off',
            }}
            required
          />
          {errors && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {errors}
            </Typography>
          )}
          <Stack spacing={2} mt={2}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              fullWidth={isMobile}
              sx={{ marginTop: 2, minWidth: '80px' }}
            >
              Log In
            </Button>
            <Typography variant="body2">
              <Link
                to="/forgot-password"
                href="/forgot-password"
                ml={1}
                underline="hover"
              >
                Forgot your password?
              </Link>
            </Typography>
          </Stack>
        </form>

        <Box mt={3}>
          <Typography mt={2} variant="body2">
            Don&apos;t have an account yet?
            <Link to="/register" href="/register" ml={1} underline="hover">
              Register
            </Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default Login;
